import type { Forecast, Station, Summary } from '../api.ts';
import { el, escapeHtml, nl } from '../components/ui.ts';

/** Open-Meteo's weercodes naar Nederlands. */
function codeText(code: number | null): string {
  if (code === null) return '';
  if (code === 0) return 'onbewolkt';
  if (code <= 2) return 'licht bewolkt';
  if (code === 3) return 'bewolkt';
  if (code <= 48) return 'nevel';
  if (code <= 57) return 'motregen';
  if (code <= 67) return 'regen';
  if (code <= 77) return 'sneeuw';
  if (code <= 82) return 'buien';
  if (code <= 86) return 'sneeuwbuien';
  return 'onweer';
}

/**
 * Voettekst met de standplaats en een vijfdaagse voorspelling.
 *
 * In Navy is dit geen donker blok meer onderaan een lichte pagina maar hetzelfde
 * vlak als de rest, afgescheiden door één stevige lijn. Er is daarom ook geen
 * aparte kleurschaal meer voor nodig — zie `.site-foot` in styles.css.
 *
 * Het adres komt uit de naam die op dev.netatmo.com is ingesteld; de
 * coördinaten en hoogte komen uit `place` in de API. Bewust niet uit het
 * ontwerp overgenomen: dat had coördinaten die er zes kilometer naast lagen.
 */
export function footerSection(
  station: Station,
  forecast: Forecast,
  summary: Summary,
  coverage: { from: string; to: string } | null,
): HTMLElement {
  const foot = el('footer', 'site-foot');
  const inner = el('div', 'inner');

  inner.append(placeBlock(station), forecastBlock(forecast));
  foot.append(inner);

  const since = coverage ? coverage.from.split('-').reverse().join('/') : null;
  foot.append(
    el(
      'div',
      'bar',
      `<div class="inner2">
         <span>Weerstation · eigen dashboard bovenop de Netatmo Weather API</span>
         <span>${since ? `Data sinds ${escapeHtml(since)} · ` : ''}${summary.measuredDays.toLocaleString('nl-BE')} meetdagen · elk uur ververst</span>
       </div>`,
    ),
  );

  return foot;
}

function placeBlock(station: Station): HTMLElement {
  const block = el('div');

  // De stationsnaam draagt bij dit station het adres; het achtervoegsel
  // "(Station)" dat Netatmo eraan plakt hoort niet in beeld.
  const name = (station?.name ?? '').replace(/\s*\(Station\)\s*$/i, '').trim();
  const heading = name || station?.city || 'Onbekend';
  block.append(el('h3', undefined, escapeHtml(heading).replace(/\s+(\d{4}\s)/, '<br>$1')));

  const bits: string[] = [];
  if (station?.latitude != null && station.longitude != null) {
    const ns = station.latitude >= 0 ? 'N' : 'Z';
    const ew = station.longitude >= 0 ? 'O' : 'W';
    bits.push(
      `${nl(Math.abs(station.latitude), 4)}° ${ns}, ${nl(Math.abs(station.longitude), 4)}° ${ew}`,
    );
  }
  if (station?.altitude != null) bits.push(`${nl(station.altitude, 0)} m boven zeeniveau`);

  block.append(el('div', 'sub', escapeHtml(bits.join(' · '))));

  if (station?.latitude != null && station.longitude != null) {
    const q = `${station.latitude},${station.longitude}`;
    const map = el(
      'div',
      'map',
      `<iframe
         src="https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed"
         loading="lazy"
         referrerpolicy="no-referrer"
         title="Kaart van de standplaats"></iframe>`,
    );
    block.append(map);
  }

  return block;
}

function forecastBlock(forecast: Forecast): HTMLElement {
  const block = el('div');
  // Geen "VOORSPELLING"-kop meer: zonder de tegenhanger links links zouden de
  // twee kolommen op verschillende hoogte beginnen.
  block.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;flex-wrap:wrap">
      <h3 style="margin-top:0">Vijf dagen vooruit</h3>
      <div style="font-size:12px;color:var(--ink-4)">bron: Open-Meteo</div>
    </div>`;

  if (forecast.days.length === 0) {
    block.append(
      el('div', 'sub', escapeHtml(forecast.error ?? 'Voorspelling niet beschikbaar')),
    );
    return block;
  }

  const grid = el('div', 'fc-grid');
  for (const day of forecast.days) {
    const date = new Date(`${day.date}T12:00:00`);
    grid.append(
      el(
        'div',
        'fc',
        `<div class="d">${escapeHtml(date.toLocaleDateString('nl-BE', { weekday: 'short' }))}</div>
         <div class="dt">${escapeHtml(date.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' }))}</div>
         <div class="mx">${day.max === null ? '—' : `${Math.round(day.max)}°`}</div>
         <div class="mn">${day.min === null ? '' : `${Math.round(day.min)}°`}</div>
         <div class="cd">${escapeHtml(codeText(day.code))}<br>${day.rain === null ? '' : `${nl(day.rain)} mm`}</div>`,
      ),
    );
  }
  block.append(grid);
  return block;
}
